import { Modal, Text, List, Alert } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { CREATE_DOCUMENT_EXPORT } from 'src/graphql/documents.graphql'
import IconInfoCircle from 'src/icons/IconInfoCircle'

interface Document {
  documentId: string
  fileName: string
  category: string
}

interface ExportDocumentsModalProps {
  isOpen: boolean
  onClose: () => void
  applicantId: string
  selectedDocumentIds: string[]
  selectedDocuments: Document[]
}

const ExportDocumentsModal = ({
  isOpen,
  onClose,
  applicantId,
  selectedDocumentIds,
  selectedDocuments,
}: ExportDocumentsModalProps) => {
  const [createExport, { loading }] = useMutation(CREATE_DOCUMENT_EXPORT, {
    onCompleted: () => {
      toast(
        'Export started! You will receive an email with the download link shortly.',
        'success'
      )
      onClose()
    },
    onError: (error) => {
      toast(error.message || 'Failed to start export', 'error')
    },
  })

  const handleExport = () => {
    createExport({
      variables: {
        input: {
          applicantId,
          documentIds: selectedDocumentIds,
        },
      },
    })
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Export Documents"
      size="md"
      classNames={{
        title: 'text-lg font-semibold text-doubleNickel-gray-900',
      }}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="mb-2 text-xs font-semibold text-doubleNickel-gray-700">
            What happens next:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs text-doubleNickel-gray-700">
            <li>
              Password-protected ZIP created with {selectedDocuments.length}{' '}
              document(s)
            </li>
            <li>Email sent with password (within minutes)</li>
            <li>
              ZIP file appears on this applicant's Documents page (available for
              24 hours)
            </li>
          </ul>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold text-doubleNickel-gray-700">
            Selected documents:
          </p>
          <div className="max-h-60 overflow-y-auto rounded border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 p-3">
            <List size="xs" className="text-xs text-doubleNickel-gray-700">
              {selectedDocuments.map((doc, index) => (
                <List.Item key={doc.documentId}>
                  <span className="font-medium">{index + 1}.</span>{' '}
                  <span className="font-medium">{doc.category}</span> -{' '}
                  {doc.fileName}
                </List.Item>
              ))}
            </List>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <DNButton variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </DNButton>
          <DNButton variant="primary" onClick={handleExport} disabled={loading}>
            Start Export
          </DNButton>
        </div>
      </div>
    </Modal>
  )
}

export default ExportDocumentsModal
